import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportFlowPages,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { OnboardStatusAiOverview, OnboardStatusResponse, OnboardStatusRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function daysLabel(days: number | null | undefined): string {
  if (days == null) return '';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return `${days}d left`;
}

function readinessLabel(readiness?: OnboardStatusAiOverview['readiness'] | null): string {
  if (!readiness) return 'No AI';
  return readiness.replace(/_/g, ' ');
}

function fmtWage(row: OnboardStatusRow): string {
  if (row.wage == null) return '—';
  const formatted = row.wage.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return row.wageCurrency ? `${formatted} ${row.wageCurrency}` : formatted;
}

function BulletCol({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: 'ok' | 'warn' | 'info';
}) {
  return (
    <div className={`onboard-status-ai-col onboard-status-ai-col-${tone}`}>
      <p className="onboard-status-ai-col-title">{title}</p>
      {items.length ? (
        <ul>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="onboard-status-ai-empty">None</p>
      )}
    </div>
  );
}

function CrewBlock({ row, certWithin }: { row: OnboardStatusRow; certWithin: number }) {
  const ai = row.aiOverview;
  const coeHint = daysLabel(row.daysToCoe);
  const medHint = daysLabel(row.daysToMedical);
  return (
    <section className="onboard-status-crew">
      <div className="onboard-status-crew-head">
        <div>
          <p className="onboard-status-crew-name">
            {row.name}
            {row.crewRef ? <span className="onboard-status-crew-ref">{row.crewRef}</span> : null}
            {row.notRecommended ? (
              <span className="onboard-status-badge onboard-status-badge-danger">Not recommended</span>
            ) : null}
          </p>
          <p className="onboard-status-crew-meta">
            {[row.rank, row.nationality, row.age != null ? `Age ${row.age}` : null]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
        </div>
        <span
          className={`onboard-status-badge onboard-status-badge-${
            ai?.readiness === 'READY'
              ? 'ok'
              : ai?.readiness === 'CONDITIONAL'
                ? 'warn'
                : ai?.readiness === 'NOT_READY'
                  ? 'danger'
                  : 'muted'
          }`}
        >
          {readinessLabel(ai?.readiness)}
        </span>
      </div>

      <table className="onboard-status-metrics">
        <tbody>
          <tr>
            <th>Sign-on</th>
            <td>
              {fmtDate(row.signOnDate)}
              {row.monthsOnboard != null ? ` · ${Math.round(row.monthsOnboard)} mo` : ''}
            </td>
            <th>COE</th>
            <td>
              {fmtDate(row.coeDate)}
              {coeHint ? ` · ${coeHint}` : ''}
            </td>
            <th>Wage</th>
            <td>{fmtWage(row)}</td>
          </tr>
          <tr>
            <th>Certs</th>
            <td>
              {row.certsExpired} exp · {row.certsExpiring} ≤{certWithin}d · {row.certsTotal} total
            </td>
            <th>Medical</th>
            <td>
              {fmtDate(row.medicalNextDue)}
              {medHint ? ` · ${medHint}` : ''}
              {row.medicalKind ? ` · ${row.medicalKind}` : ''}
            </td>
            <th>Appraisal</th>
            <td>
              {row.appraisalAvg != null ? row.appraisalAvg.toFixed(1) : '—'}
              {row.lastAppraisalDate ? ` · ${fmtDate(row.lastAppraisalDate)}` : ''}
            </td>
          </tr>
        </tbody>
      </table>

      {ai ? (
        <>
          {ai.headline ? (
            <div className="onboard-status-ai-head">
              <p className="onboard-status-ai-headline">{ai.headline}</p>
              {ai.summary ? <p className="onboard-status-ai-summary">{ai.summary}</p> : null}
            </div>
          ) : null}
          <div className="onboard-status-ai-grid">
            <BulletCol title="Strengths" items={ai.strengths ?? []} tone="ok" />
            <BulletCol title="Risks" items={ai.risks ?? []} tone="warn" />
            <BulletCol title="Recommendations" items={ai.recommendations ?? []} tone="info" />
          </div>
          <p className="onboard-status-ai-meta">
            AI {fmtDate(ai.generatedAt?.slice(0, 10))} · {ai.model}
          </p>
        </>
      ) : (
        <p className="onboard-status-ai-missing">No AI overview generated yet.</p>
      )}
    </section>
  );
}

export type OnboardStatusReportProps = {
  data: OnboardStatusResponse;
  rows: OnboardStatusRow[];
  companyLogoSrc?: string | null;
  companyName?: string | null;
  asOfLabel?: string;
};

export const OnboardStatusReport: React.FC<OnboardStatusReportProps> = ({
  data,
  rows,
  companyLogoSrc = null,
  companyName = null,
  asOfLabel,
}) => {
  const vessel = data.vessel;
  const vesselName = vessel?.vesselName || '—';
  const flag = vessel?.flag?.name || vessel?.flag?.isoCode2 || '—';
  const summary = data.summary;
  const certWithin = data.certWithinDays;
  const asOf = asOfLabel || fmtDate(new Date().toISOString().slice(0, 10));

  const reprintSummary = useMemo(
    () => ({
      crewCount: rows.length,
      withAiOverview: rows.filter((r) => r.aiOverview).length,
      ready: rows.filter((r) => r.aiOverview?.readiness === 'READY').length,
      conditional: rows.filter((r) => r.aiOverview?.readiness === 'CONDITIONAL').length,
      notReady: rows.filter((r) => r.aiOverview?.readiness === 'NOT_READY').length,
      certAttention: rows.filter((r) => r.certsExpired > 0 || r.certsExpiring > 0).length,
    }),
    [rows],
  );

  const firstHeader = useMemo(
    () => (
      <ReportHeader>
        <ReportTitleRow
          title="Onboard Status"
          formId="AI readiness"
          logoSrc={companyLogoSrc}
          logoAlt={companyName || 'Management company logo'}
        />
        <ReportMeta>
          <ReportMetaRow>
            <ReportField label="Vessel" value={vesselName} />
            <ReportField label="IMO number" value={dash(vessel?.imoNumber)} />
            <ReportField label="Call sign" value={dash(vessel?.callSign)} />
          </ReportMetaRow>
          <ReportMetaRow>
            <ReportField label="Flag" value={flag} />
            <ReportField label="As of" value={asOf} />
            <ReportField label="Cert window" value={`${certWithin} days`} />
          </ReportMetaRow>
        </ReportMeta>
        <div className="onboard-status-kpis">
          <div>
            <span className="onboard-status-kpi-label">Onboard</span>
            <span className="onboard-status-kpi-value">{reprintSummary.crewCount}</span>
          </div>
          <div>
            <span className="onboard-status-kpi-label">With AI</span>
            <span className="onboard-status-kpi-value">{reprintSummary.withAiOverview}</span>
          </div>
          <div>
            <span className="onboard-status-kpi-label">Ready</span>
            <span className="onboard-status-kpi-value onboard-status-kpi-ok">{reprintSummary.ready}</span>
          </div>
          <div>
            <span className="onboard-status-kpi-label">Conditional</span>
            <span className="onboard-status-kpi-value onboard-status-kpi-warn">
              {reprintSummary.conditional}
            </span>
          </div>
          <div>
            <span className="onboard-status-kpi-label">Not ready</span>
            <span className="onboard-status-kpi-value onboard-status-kpi-danger">
              {reprintSummary.notReady}
            </span>
          </div>
          <div>
            <span className="onboard-status-kpi-label">Cert attention</span>
            <span className="onboard-status-kpi-value onboard-status-kpi-warn">
              {reprintSummary.certAttention}
            </span>
          </div>
        </div>
        {rows.length !== summary.crewCount ? (
          <p className="onboard-status-filter-note">
            Showing filtered list ({rows.length} of {summary.crewCount} onboard).
          </p>
        ) : null}
      </ReportHeader>
    ),
    [
      asOf,
      certWithin,
      companyLogoSrc,
      companyName,
      flag,
      reprintSummary,
      rows.length,
      summary.crewCount,
      vessel?.callSign,
      vessel?.imoNumber,
      vesselName,
    ],
  );

  const continueHeader = useMemo(
    () => (
      <ReportHeader>
        <ReportTitleRow title="Onboard Status" formId="Continued" />
        <ReportMeta>
          <ReportMetaRow>
            <ReportField label="Vessel" value={vesselName} />
            <ReportField label="As of" value={asOf} />
            <ReportField label="Crew" value={`${rows.length} onboard`} />
          </ReportMetaRow>
        </ReportMeta>
      </ReportHeader>
    ),
    [asOf, rows.length, vesselName],
  );

  const lastFooter = useMemo(
    () =>
      companyName ? (
        <ReportFooter>
          <ReportNote>Issued by {companyName}</ReportNote>
        </ReportFooter>
      ) : null,
    [companyName],
  );

  const items = useMemo(
    () =>
      rows.length === 0
        ? [<p key="empty" className="onboard-status-empty">No onboard crew for this vessel.</p>]
        : rows.map((row) => <CrewBlock key={row.crewId} row={row} certWithin={certWithin} />),
    [certWithin, rows],
  );

  const itemKeys = useMemo(
    () => (rows.length === 0 ? ['empty'] : rows.map((r) => r.crewId)),
    [rows],
  );

  return (
    <ReportFlowPages
      className="onboard-status-report"
      size="portrait"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default OnboardStatusReport;
